import { APP_INIT_RESPONSE_TYPE, APP_LOGO, CUSTOMER_MEMBER_ROLE, ONESIGNAL_APP_ID } from '../global/constants';
import { decodeFirebaseCustomToken, setWorkspaceIdInAxios, setupToken } from '../helpers/authTokenHelpers';
import UserPreferenceSingleton from '../helpers/userPreferenceSingleton';
import { ROUTES } from '../routes/urls';
import { loadInboxData } from './inboxServices';
import { getInvoiceList } from './invoiceService';
import { getWorkspaceDetails, getWorkspaceList } from './workspaceService';
import { getColorWithOpacity, getCurrentDomain, getUrlParam, isDev, isEmpty, isReactNative } from '../helpers/common';
import { setAnalyticUserAndCompany } from './analyticsService';
import { captureException, setLogUser } from './logService';
import { CustomWindow } from '../interfaces/OneSignalInterface';
import { getTotalTaskCount } from './taskServices';
import { fetchGraphQL } from '../lib/contentful';
import { getFirebaseToken } from './authServices';
import { getCompanySettingIntegration, getProfileSettingData, getSubscriptionList } from './settingServices';
import { connectToWebsocket } from './realtimeServices';
import { rootStore } from '../mobx/rootStore';
import { syncProjects, syncTasks } from './syncService';
import { signInWithFirebaseCustomToken, setupFirebaseAuthListeners } from '../helpers/firebaseHelper';
import { getActiveTime } from './timeTrackingServices';
import { getViewList } from './viewServices';
import { getCustomerList } from './customerServices';
import { WorkspaceModel } from '../mobx/models/workspace';
import { UserModel } from '../mobx/models/user';
import { RawCurrentUser } from '../mobx/interfaces/user';
import { RawWorkspace } from '../mobx/interfaces/workspace';
import { UserProfileDataInterface } from '../interfaces/AuthInterface';
import { syncMessagesAndGroupsInBackground } from './chatMessageSyncService';
import { scheduleRecentConversationsPrefetch } from './conversationSyncService';

interface OneSignalTagsResponse {
  success: boolean;
  results: Record<string, { success: boolean }>;
}

interface FaviconIcon {
  url?: string;
}

interface CustomBrandingData {
  metaTitle?: string | undefined;
  faviconIcon?: FaviconIcon;
  primaryColor?: string;
  primaryDarkColor?: string;
}

declare const window: CustomWindow;

/**
 * Fetches and applies branding data based on the current hostname
 * This function queries Contentful for client configuration and applies
 * custom branding including colors, favicon, and title
 */
export const getBrandDataBasedOnHostname = async () => {
  try {
    const hostName = getCurrentDomain();
    if (hostName === 'dash.teamcamp.app' || hostName === 'beta.teamcamp.app' || hostName === 'localhost') return;
    // GraphQL query to fetch client configuration
    const query = `query{
        clientConfigurationCollection(where:{domain: "${hostName}"}){
          items{
            primaryColor
            primaryDarkColor
            domain
            faviconIcon{
              url
            }
            metaTitle
            logo{
              url
            }
          }
        }
      }`;
    const response = await fetchGraphQL(query);
    const customBrandingData = response?.data?.clientConfigurationCollection?.items?.[0];

    if (customBrandingData) {
      // Apply custom branding if available
      applyCustomBranding(customBrandingData);
      if (rootStore.workspaceStore?.currentWorkspace?.id)
        UserPreferenceSingleton.getInstance().setBranding(
          rootStore.workspaceStore?.currentWorkspace?.id,
          customBrandingData
        );
    } else {
      // Apply default branding
      applyDefaultBranding();
      if (rootStore.workspaceStore?.currentWorkspace?.id)
        UserPreferenceSingleton.getInstance().setBranding(rootStore.workspaceStore?.currentWorkspace?.id, {});
    }
  } catch (e) {
    captureException(e);
  }
};

/**
 * Applies custom branding data to the application
 * @param customBrandingData - The branding data to apply
 */
export const applyCustomBranding = (customBrandingData: CustomBrandingData) => {
  rootStore.appStore.setCustomBranding(customBrandingData);

  // Set document title
  document.title = customBrandingData?.metaTitle ?? 'Teamcamp';

  // Set favicon
  updateFavicon(customBrandingData?.faviconIcon?.url);

  // Apply light mode colors
  applyLightModeColors(customBrandingData?.primaryColor);

  // Apply dark mode colors
  applyDarkModeColors(customBrandingData?.primaryDarkColor);
};

/**
 * Updates the favicon with the provided URL or default
 * @param faviconUrl - URL of the custom favicon
 */
const updateFavicon = (faviconUrl?: string): void => {
  const favicon: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');
  const defaultFavicon = APP_LOGO;

  if (favicon) {
    favicon.href = faviconUrl || defaultFavicon;
  }
};

/**
 * Applies light mode color theme
 * @param primaryColor - Primary color for light mode
 */
const applyLightModeColors = (primaryColor?: string): void => {
  const root = document.querySelector('.light') as HTMLElement;
  if (!root) return;

  if (primaryColor) {
    const hoverColor = getColorWithOpacity(primaryColor);
    root.style.setProperty('--brand-primary', primaryColor);
    root.style.setProperty('--brand-primary-hover', !isEmpty(hoverColor) ? hoverColor : primaryColor);
  } else {
    // Default colors
    root.style.setProperty('--brand-primary', '#14151A');
    root.style.setProperty('--brand-primary-hover', '#14151AE6');
  }
};

/**
 * Applies dark mode color theme
 * @param primaryDarkColor - Primary color for dark mode
 */
const applyDarkModeColors = (primaryDarkColor?: string): void => {
  const darkRoot = document.querySelector('.dark') as HTMLElement;
  if (!darkRoot) return;

  if (primaryDarkColor) {
    const hoverColor = getColorWithOpacity(primaryDarkColor);
    darkRoot.style.setProperty('--brand-primary', primaryDarkColor);
    darkRoot.style.setProperty('--brand-primary-hover', !isEmpty(hoverColor) ? hoverColor : primaryDarkColor);
  } else {
    // Default colors
    darkRoot.style.setProperty('--brand-primary', '#14151A');
    darkRoot.style.setProperty('--brand-primary-hover', '#14151AE6');
  }
};

/**
 * Applies default branding when no custom branding is available
 */
export const applyDefaultBranding = () => {
  const favicon: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');
  if (favicon?.href) {
    favicon.href = APP_LOGO;
  }

  document.title = 'Teamcamp';
  rootStore.appStore.setCustomBranding({});

  // Apply default light mode colors
  const root = document.querySelector('.light');
  if (root && root instanceof HTMLElement) {
    root.style.setProperty('--brand-primary', '#14151A');
    root.style.setProperty('--brand-primary-hover', '#14151AE6');
  }

  // Apply default dark mode colors
  const darkRoot = document.querySelector('.dark');
  if (darkRoot && darkRoot instanceof HTMLElement) {
    darkRoot.style.setProperty('--brand-primary', '#14151A');
    darkRoot.style.setProperty('--brand-primary-hover', '#14151AE6');
  }
};

/**
 * Fetches and applies branding data based on workspace ID
 * @param workspaceId - The ID of the workspace
 */
export const getBrandDataBasedOnWorkspaceId = async (workspaceId: string) => {
  try {
    if (isEmpty(workspaceId)) return;

    // GraphQL query to fetch client configuration for workspace
    const query = `query{
        clientConfigurationCollection(where:{workspaceId: "${workspaceId}"}){
          items{
            primaryColor
            primaryDarkColor
            domain
            faviconIcon{
              url
            }
            metaTitle
            logo{
              url
            }
          }
        }
      }`;
    const response = await fetchGraphQL(query);
    const customBrandingData = response?.data?.clientConfigurationCollection?.items?.[0];

    if (customBrandingData) {
      // Apply custom branding if available
      applyCustomBranding(customBrandingData);
      UserPreferenceSingleton.getInstance().setBranding(workspaceId, customBrandingData);
    } else {
      // Fall back to hostname-based branding
      const hostName = getCurrentDomain();
      if (hostName === 'dash.teamcamp.app') {
        applyDefaultBranding();
        UserPreferenceSingleton.getInstance().setBranding(workspaceId, {});
      } else {
        getBrandDataBasedOnHostname();
      }
    }
  } catch (e) {
    captureException(e);
  }
};

/**
 * Loads Firebase token for a workspace
 * @param workspaceId - The ID of the workspace
 */
const loadWorkspaceFirebaseToken = async (workspaceId: string) => {
  try {
    // Try using locally stored custom token first
    const firebaseCustomToken = decodeFirebaseCustomToken(workspaceId);
    if (firebaseCustomToken) {
      try {
        await signInWithFirebaseCustomToken(firebaseCustomToken);
        setupFirebaseAuthListeners();
        return;
      } catch (e) {
        // Fall back to fetching a fresh custom token below
      }
    }

    // Fallback: fetch fresh custom token from backend and sign in
    const firebaseToken = await getFirebaseToken(workspaceId);
    await signInWithFirebaseCustomToken(firebaseToken);
    // Set up token refresh listeners
    setupFirebaseAuthListeners();
  } catch (e) {
    captureException(e);
  }
};

/**
 * Loads initial workspace details
 * @param workspaceId - The ID of the workspace
 * @returns Project list response
 */
export const loadInitWorkspaceDetails = async (workspaceId: string) => {
  try {
    rootStore.appStore.setIsAppLoading(true);
    if (workspaceId) {
      loadWorkspaceFirebaseToken(workspaceId);
      await syncProjects({ loadCached: true });
      return rootStore.projectStore.projects || [];
    }
  } catch (e) {
    console.log('Error from load workspace details');
  } finally {
    rootStore.appStore.setIsAppLoading(false);
  }
};

/**
 * Initializes a workspace based on user preferences or environment
 * OPTIMIZED: Uses cached workspace from localStorage first for returning users,
 * then verifies with Firebase/API in background
 * @returns Workspace details (cached immediately, fresh data synced in background)
 */
const initializeWorkspace = async (skipWorkspaceList: boolean, useCachedWorkspaceList: boolean) => {
  const cachedWorkspace = UserPreferenceSingleton.getInstance().getWorkspace();

  /**
   * This logic added because we want to switch workspace when use click on different workspace notification
   * At that time will update workspace to get wid in URL search params
   */
  const widFromUrl = getUrlParam(window.location.search, 'wid');
  if (widFromUrl && widFromUrl !== cachedWorkspace?.id) {
    await getWorkspaceDetails(widFromUrl);
    return UserPreferenceSingleton.getInstance().getWorkspace();
  }

  const loadAndReturnCurrentWorkspace = async () => {
    await getWorkspaceList();
    const currentWorkspace = UserPreferenceSingleton.getInstance().getWorkspace();
    if (currentWorkspace?.id) {
      const workspaceDetails = await getWorkspaceDetails(currentWorkspace.id).catch((e) => {
        captureException(e);
      });
      return workspaceDetails;
    }
    return null;
  };

  if (cachedWorkspace?.id) {
    if (cachedWorkspace?.users?.length > 0) {
      // Refresh the workspace list and current workspace details together in the background.
      Promise.all([
        skipWorkspaceList ? Promise.resolve() : getWorkspaceList({ useCache: useCachedWorkspaceList }),
        getWorkspaceDetails(cachedWorkspace.id)
      ]).catch((e) => {
        captureException(e);
      });
      return cachedWorkspace;
    }
    if (!skipWorkspaceList) getWorkspaceList({ useCache: useCachedWorkspaceList });
    await getWorkspaceDetails(cachedWorkspace.id);
    return UserPreferenceSingleton.getInstance().getWorkspace();
  }
  return loadAndReturnCurrentWorkspace();
};

/**
 * Initializes the application
 * OPTIMIZED: Split into critical path (cached data) vs background operations
 * Critical path loads cached projects/tasks from IndexedDB for fast UI render
 * Non-critical operations (branding, analytics, views) run in background
 *
 * @param isInitLoad - Whether this is the initial load
 * @param skipWorkspaceList - Skip refetching the workspace list when it is already loaded (e.g. workspace switch)
 * @returns Response type and redirect path if needed
 */
export const appInit = async (isInitLoad = false, skipWorkspaceList = false) => {
  try {
    rootStore.appStore.setIsAppLoading(true);

    // Check authentication token (sync - instant)
    const token = setupToken();
    if (!token) return;

    // Get user details (sync - from localStorage)
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    if (userDetails) {
      setLogUser(userDetails);
      // Non-blocking: profile settings can load in background
      if (isInitLoad) getProfileSettingData();
      // Non-blocking: FCM setup
      setupFCMNotifications(userDetails);
    }

    // Initialize workspace (OPTIMIZED: uses cache for returning users)
    // Only a page load reuses the stored workspace list; login and other flows fetch it fresh
    const workspaceDetails = await initializeWorkspace(skipWorkspaceList, isInitLoad);
    if (!workspaceDetails) return { type: APP_INIT_RESPONSE_TYPE.REDIRECT, path: ROUTES.WORKSPACE };

    // Set up workspace in stores and services
    setupWorkspace(workspaceDetails, userDetails);

    // CRITICAL PATH: Only wait for cached data from IndexedDB
    // This is fast (~50-100ms) as it reads from local storage
    await Promise.all([syncProjects({ loadCached: true }), syncTasks({ loadCached: true })]);

    // UI can now render with cached data - unblock the app
    rootStore.appStore.setIsAppLoading(false);

    // NON-CRITICAL: Load remaining data in background (non-blocking)
    // These operations don't block user interaction
    loadNonCriticalData(workspaceDetails, userDetails);

    const projectResponse = rootStore.projectStore.projects || [];

    // Check if user should be redirected to welcome page
    if (
      projectResponse?.length === 0 &&
      workspaceDetails?.customerRole !== CUSTOMER_MEMBER_ROLE.PROJECT_ONLY &&
      workspaceDetails?.customerRole !== CUSTOMER_MEMBER_ROLE.FULL_ACCESS &&
      (workspaceDetails?.isAdmin || workspaceDetails?.isOwner)
    ) {
      return { type: APP_INIT_RESPONSE_TYPE.SUCCESS };
    } else {
      return { type: APP_INIT_RESPONSE_TYPE.SUCCESS };
    }
  } catch (e) {
    captureException(e);
  } finally {
    // Ensure loading state is cleared even if already set above
    rootStore.appStore.setIsAppLoading(false);
  }
};

/**
 * Loads non-critical data in background after app shell is rendered
 * These operations don't block the user from interacting with the app
 */
const loadNonCriticalData = (workspaceDetails: WorkspaceModel, userDetails: RawCurrentUser) => {
  const workspaceId = workspaceDetails?.id;

  // Run all these background loads together instead of one after another.
  const operations = [
    getBrandDataBasedOnWorkspaceId(workspaceId),
    setAnalyticUserAndCompany(userDetails, workspaceDetails),
    getViewList(),
    getActiveTime(),
    syncMessagesAndGroupsInBackground().then((synced) => {
      if (synced) scheduleRecentConversationsPrefetch();
    }),
    getCompanySettingIntegration(workspaceId),
    getTotalTaskCount(),
    connectToWebsocket(workspaceId, userDetails?.id),
    getSubscriptionList()
  ];

  // Load inbox data if not on home page
  if (window.location.pathname !== '/') {
    operations.push(loadInboxData());
  }

  // Admin-only data
  if (workspaceDetails?.isAdmin === true) {
    operations.push(getInvoiceList(workspaceDetails), getCustomerList());
  }

  Promise.all(operations).catch((e) => {
    captureException(e);
  });
};

/**
 * Sets up FCM notifications for the user
 * @param userDetails - User details
 */
const setupFCMNotifications = (userDetails: UserModel): void => {
  if (window.electronApi?.subscribeFcm) {
    window.electronApi.subscribeFcm();
  }

  if (isReactNative()) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'subscribe-fcm', data: { accessToken: userDetails.access_token } })
    );
  }
};

/**
 * Sets up workspace in stores and services
 * @param workspaceDetails - Workspace details
 * @param userDetails - User details
 */
const setupWorkspace = (workspaceDetails: RawWorkspace, userDetails: UserProfileDataInterface): void => {
  rootStore.workspaceStore.setCurrentWorkspace(workspaceDetails);
  setWorkspaceIdInAxios(workspaceDetails?.id);
  loadWorkspaceFirebaseToken(workspaceDetails?.id);

  rootStore.setCurrentUser(userDetails);
};

/**
 * Initializes push notifications using OneSignal
 */
export const initPushNotification = async () => {
  try {
    const OneSignal = window.OneSignal || [];
    if (!OneSignal) return;

    if (!ONESIGNAL_APP_ID) return;
    if (!window.location.origin.startsWith('https://dash.teamcamp.app')) return;

    const onesignalInit = UserPreferenceSingleton.getInstance().getOneSignalInit();
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();

    // Initialize OneSignal if not already initialized
    if (!onesignalInit) {
      await initializeOneSignal();
    }

    // Set user tags if user is logged in
    if (!userDetails) return;
    setUserTags(userDetails);

    // Set up notification URL and event handlers
    setupNotificationHandlers();
  } catch (e) {
    captureException(e);
  }
};

/**
 * Initializes OneSignal for push notifications
 */
const initializeOneSignal = async (): Promise<void> => {
  await window.OneSignal?.push(async function () {
    await window.OneSignal?.init({
      appId: ONESIGNAL_APP_ID,
      autoRegister: true,
      notifyButton: {
        enable: false
      }
    })
      .then(async () => {
        UserPreferenceSingleton.getInstance().setOneSignalInit(true);
        window.OneSignal?.showSlidedownPrompt();
      })
      .catch((error: Error) => {
        captureException(error);
      });
  });
};

/**
 * Sets user tags for OneSignal
 * @param userDetails - User details
 */
const setUserTags = (userDetails: UserModel): void => {
  const userTagPayload = {
    email: userDetails.email,
    userid: userDetails.id
  };

  try {
    window.OneSignal?.push(function () {
      window.OneSignal?.sendTags(userTagPayload)
        .then(function (tagsSent: OneSignalTagsResponse) {})
        .catch(function (error: Error) {
          console.error('Error sending tags:', error);
        });
    });
  } catch (e) {
    captureException(e);
  }
};

/**
 * Sets up notification URL and event handlers
 */
const setupNotificationHandlers = (): void => {
  try {
    // Set default notification URL
    let notificationUrl = 'https://dash.teamcamp.app';
    if (isDev()) {
      notificationUrl = 'http://localhost:8080';
    }
    window.OneSignal?.push(function () {
      window.OneSignal?.setDefaultNotificationUrl(notificationUrl);
    });
  } catch (e) {
    captureException(e);
  }

  // Set up notification event handlers
  window.OneSignal?.push(function () {
    window.OneSignal?.isPushNotificationsEnabled(function (isEnabled: boolean) {});
  });

  window.OneSignal?.push(function () {
    window.OneSignal?.on('notificationDisplay', function (event: Event) {});
  });

  window.OneSignal?.push(function () {
    window.OneSignal?.on('notificationClicked', function (event: Event) {
      console.log('Notification clicked:', event);
    });
  });
};

export function dispatchAppError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}

export function dispatchAppSuccess(msg: string) {
  rootStore.alertNotificationStore.setSuccessMessage(msg);
}
